import { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';

import { Observer, Equator, Horizon, DefineStar } from 'astronomy-engine';
import { sunInfo, LocalSiderealTime } from "../utils/functions";

import moment from "moment";
var momentRange = require('moment-range');
momentRange.extendMoment(moment);

const darkTheme = {
  chart: {
    backgroundColor: '#212529',
    style: {
      fontFamily: "'Roboto Condensed', sans-serif",
      fontSize: '16px',
      color: '#eeeeee'
    }
  },
  title: {
    style: {
      color: '#ffffff',
      opacity: 0.4
    }
  },
  xAxis: {
    gridLineColor: '#444444',
    labels: {
      style: {
        color: '#eeeeee'
      }
    },
    lineColor: '#777777',
    tickColor: '#777777',
    title: {
      style: {
        color: '#ffffff'
      }
    }
  },
  yAxis: {
    gridLineColor: '#444444',
    labels: {
      style: {
        color: '#dddddd'
      }
    },
    lineColor: '#777777',
    tickColor: '#777777',
    title: {
      style: {
        color: '#eeeeee'
      }
    }
  },
  legend: {
    enabled: false,
    itemStyle: {
      color: '#eeeeee'
    },
    itemHoverStyle: {
      color: '#aaaaaa'
    }
  },
  tooltip: {
    backgroundColor: '#333333',
    style: {
      color: '#ffffff'
    }
  },
  plotOptions: {
    series: {
      dataLabels: {
        color: '#ffffff'
      }
    }
  }
};

Highcharts.setOptions(darkTheme);

const Staralt = ({
  containerId = 'moonalt-container',
  observer_location,
  obsdate_yyyymmdd,
  div_height = "300px",
  minimum_altitude = 0,
  xtick_interval = 10,
  night_mode = "True",
  targets = []
}) => {
  const chartRef = useRef(null);

  // Fuerza la recarga cada 5min sin modificar variables
  const [reload, setReload] = useState(0);

  useEffect(() => {
    // Configurar intervalo para actualizar cada 5 minutos
    const intervalId = setInterval(() => {
      setReload(prev => prev + 1);
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const local_time = moment().tz(observer_location['timezone']);
    const current_utc = moment().utc().unix() * 1000;
    const local_noon_hour = Math.abs(local_time.utcOffset() / 60.);
    
    let hour_shift;
    if (local_noon_hour > 6) {
        hour_shift = 0;
    } else {
        hour_shift = 12;
    }
    
    const obsdate = moment.utc(obsdate_yyyymmdd).set({hour: hour_shift, minute: 0, second: 0, millisecond: 0});
    const nextDay = moment.utc(obsdate_yyyymmdd).set({hour: hour_shift, minute: 0, second: 0, millisecond: 0}).add(1, 'days');

    let observer = new Observer(
      observer_location['latitude'],
      observer_location['longitude'],
      observer_location['elevation']
    );
                                      
    let sun_info = sunInfo(observer, obsdate.toDate());
    let sun_info_next_day = sunInfo(observer, nextDay.toDate());

    let plot_start, plot_end;

    // En modo noche el gráfico empieza 1h de la puesta de sol y termina 1h después de la salida
    // En modo regular (tiempo real), empieza 2h de UTC y termina 10h después de UTC
    if (night_mode == "True") {
      plot_start = sun_info['sunset'].clone();
      plot_start.add(-1, 'hours');
      plot_end = sun_info_next_day['sunrise'].clone();
      plot_end.add(+1, 'hours');
    } else {
      plot_start = moment.utc().add(-2, 'hours');
      plot_end = moment.utc().add(+10, 'hours');
    }

    const range = moment.range(plot_start, plot_end);

    // Targets opcionales en el modo noche
    const series = [];

    for (let i = 0; i < targets.length; i += 1) {
      try {
        let body = "Star" + (i + 1).toString();
        let star = new DefineStar(body, targets[i]['ra'] / 15, targets[i]['dec'], 1000);

        const altaz = [];

        for (const dateRange of range.by('minutes')) {
          let date = dateRange.utc().toDate();
          let equ_ofdate = Equator(body, date, observer, true, true);
          let hor = Horizon(date, observer, equ_ofdate.ra, equ_ofdate.dec, 'normal');

          altaz.push({x: (moment(date).unix()) * 1000, y: hor.altitude, azimuth: hor.azimuth});
        }

        let zones = [];
        series.push({'name': targets[i]['name'], 'data': altaz, 'zones': zones, zoneAxis: 'x'});
      } catch (error) {
        console.error(error);
      }
    }

    // Sun and Moon altitude curves
    const sun_altaz = [];
    const moon_altaz = [];

    for (const dateRange of range.by('minutes')) {
      let date = dateRange.utc().toDate();

      // Sun
      let sun_equ_ofdate = Equator('Sun', date, observer, true, true);
      let sun_hor = Horizon(date, observer, sun_equ_ofdate.ra, sun_equ_ofdate.dec, 'normal');
      sun_altaz.push({x: (moment(date).unix()) * 1000, y: sun_hor.altitude, azimuth: sun_hor.azimuth});

      // Moon
      let moon_equ_ofdate = Equator('Moon', date, observer, true, true);
      let moon_hor = Horizon(date, observer, moon_equ_ofdate.ra, moon_equ_ofdate.dec, 'normal');
      moon_altaz.push({x: (moment(date).unix()) * 1000, y: moon_hor.altitude, azimuth: moon_hor.azimuth});
    }

    // Si es modo diurno, añade el plot del sol
    if (night_mode == "False") {
      series.push({'name': 'Sun', 'data': sun_altaz, color: 'yellow', lineWidth: 1, dashStyle: 'Solid', opacity: 0.4, id: 'sun'});
    }

    // Añade el plot de la luna
    series.push({'name': 'Moon', 'data': moon_altaz, color: '#cccccc', lineWidth: 1, dashStyle: 'ShortDashDot', opacity: 0.4, id: 'moon'});

    if (chartRef.current) {
      Highcharts.chart(chartRef.current, {
        title: {
          text: ""
        },
        subtitle: {
          // text: 'Altitude and azimuth'
        },
        tooltip: {
          useHTML: true,
          padding: 0,
          formatter: function() {
            return '<div style="padding: 8px 8px 5px 8px; background-color: '+ this.series.color+' !important!;" class="tootip_header"><b>'+ this.series.name +'</b></div>'+
                    '<div class="tooltip_container" style="padding: 5px 8px 8px 8px; ">' +
                    '  <div class="tootip_body"><b>Altitude:</b> ' + Highcharts.numberFormat(this.y, 1) + 'º' +
                    '     <br><b>Azimuth</b>: '+ Highcharts.numberFormat(this.point.azimuth, 1) + 'º' +
                    '     <br><b>Local time:</b> ' + moment.utc(this.x).tz(observer_location['timezone']).format("HH:mm") + 
                    '  </div>' +
                    '</div>';
          }
        },
        yAxis: {
          title: {
            text: 'Altitude (degrees)'
          },
          tickInterval: xtick_interval,
          gridLineWidth: 0,
          min: minimum_altitude,
          max: 90,
          plotBands: [
            {
              color: 'rgba(0, 0, 0, 0.3)',
              from: -30,
              to: 0,
              zIndex: 10,
            },
          ]
        },
        xAxis: [
          {
            title: {
              text: obsdate.format("dddd, MMMM Do YYYY") + " (UT)"
            },
            type: "datetime",
            tickInterval: 5000 * 1000,
            min: plot_start.unix() * 1000, 
            max: plot_end.unix() * 1000,
            // Bandas de crepusculos
            plotBands: [
              {
                color: '#A0522D',
                from: plot_start.unix() * 1000,
                to: sun_info['sunset'].unix() * 1000,
                tooltipText: 'Before sunset',
                zIndex: -9999,
              },
              {
                color: '#254A6A',
                from: sun_info['sunset'].unix() * 1000,
                to: sun_info['civil_twilight_dusk'].unix() * 1000,
                tooltipText: 'Civil twilight',
                zIndex: -9999,
              },
              {
                color: '#333',
                from: sun_info['civil_twilight_dusk'].unix() * 1000,
                to: sun_info['nautical_twilight_dusk'].unix() * 1000,
                tooltipText: 'Nautical twilight',
                zIndex: -9999,
              },
              {
                color: '#292929', 
                from: sun_info['nautical_twilight_dusk'].unix() * 1000,
                to: sun_info['astronomical_twilight_dusk'].unix() * 1000,
                tooltipText: 'Astronomical twilight',
                zIndex: -9999,
              },
              {
                color: '#292929',
                from: sun_info_next_day['astronomical_twilight_down'].unix() * 1000,
                to: sun_info_next_day['nautical_twilight_down'].unix() * 1000,
              },
              {
                color: '#333',
                from: sun_info_next_day['nautical_twilight_down'].unix() * 1000,
                to: sun_info_next_day['civil_twilight_down'].unix() * 1000,
              },
              {
                color: '#254A6A',
                from: sun_info_next_day['civil_twilight_down'].unix() * 1000,
                to: sun_info_next_day['sunrise'].unix() * 1000,
              },
              {
                color: '#A0522D',
                from: sun_info_next_day['sunrise'].unix() * 1000,
                to: plot_end.unix() * 1000
              }
            ],
            plotLines: [
              // Línea vertical que indica la UTC en tiempo real
              {
                color: 'rgba(243, 242, 183, 0.6)',
                width: 1,
                dashStyle: 'Dot',
                value: current_utc,
                label: {
                  text: 'UTC ',
                  rotation: 270,
                  textAlign: 'center',
                  x: -5,
                  y: 20,
                  style: {
                    fontSize: '12px',
                    color: 'rgba(243, 242, 183, 0.6)',
                    fontWeight: 'normal',
                  }
                }
              },
            ],
            labels: {
              useHTML: true,
              formatter: function() {
                let date = moment(this.value);
                return date.utc().format('H[<sup>h</sup>]');
              } 
            },
            accessibility: {
              rangeDescription: 'Sun and moon'
            }
          },
          // Eje X superior con tiempo sidereo 
          {
            title: {
              text: "Local sidereal time"
            },
            type: "datetime",
            tickInterval: 5000 * 1000,
            min: plot_start.unix() * 1000,
            max: plot_end.unix() * 1000,
            opposite: true,
            labels: {
              formatter: function() {
                let date = new Date(this.value);
                let lst_date = LocalSiderealTime(date, observer_location['longitude']);
                return lst_date.format('HH:mm');
              } 
            }
          }
        ],
        legend: {
          enabled: true,
          layout: 'horizontal',
          align: 'center',
          verticalAlign: 'bottom',
          x: 0,
          y: 0,
        },
        plotOptions: {
          series: {
            label: {
              connectorAllowed: true
            },
          }
        },
        series: series,
        responsive: {
          rules: [{
            condition: {
              maxWidth: 500
            },
            chartOptions: {
              legend: {
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom'
              }
            }
          }]
        },
        credits: {
          enabled: false,
        }
      });
    }
  }, [observer_location, obsdate_yyyymmdd, reload, targets]);

  return (<div ref={chartRef} id={containerId} className='' style={{height: div_height}} />);
};

export default Staralt;